import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import 'swiper/css';
import 'swiper/css/navigation';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import { useCart } from '../context/CartContext';
import { formatCurrencyShort } from '../utils/currency';
import flatpickr from 'flatpickr';
import { WOW } from 'wowjs';
import 'animate.css';

/* ─── DATA ─────────────────────────────────────── */
const rooms = [
  {
    id: 'r1',
    img: '/images/gw/rooms/room-standard.jpg',
    title: 'Greatwood Standard',
    price: 40000,
    tag: 'Standard',
    color: '#2d7a4f',
    type: 'room',
  },
  {
    id: 'r2',
    img: '/images/gw/rooms/room-executive.jpg',
    title: 'Greatwood Executive',
    price: 50000,
    tag: 'Executive',
    color: '#1565c0',
    type: 'room',
  },
  {
    id: 'r3',
    img: '/images/gw/rooms/room-special.jpg',
    title: 'Greatwood Special',
    price: 80000,
    tag: 'Special',
    color: '#7b1fa2',
    type: 'room',
  },
  {
    id: 'r4',
    img: '/images/gw/rooms/suite-sitting.jpg',
    title: 'Presidential Suite',
    price: 150000,
    tag: 'Presidential',
    color: '#b08d57',
    type: 'room',
  },
];

const facilities = [
  {
    img: '/images/gw/facilities/reception-staff.jpg',
    icon: 'fas fa-concierge-bell',
    title: 'Reception & Lobby',
    desc: '24h grand marble lobby with professional staff',
  },
  {
    img: '/images/gw/facilities/swimming-pool.jpg',
    icon: 'fas fa-water-ladder',
    title: 'Swimming Pool',
    desc: 'Outdoor pool with fountain jets',
  },
  {
    img: '/images/gw/facilities/event-hall.jpg',
    icon: 'fas fa-champagne-glasses',
    title: 'Event Hall',
    desc: 'Banquets, weddings & conferences up to 500',
  },
  {
    img: '/images/gw/facilities/boardroom.jpg',
    icon: 'fas fa-briefcase',
    title: 'Boardroom',
    desc: 'Professional meeting space, 20 seats',
  },
  {
    img: '/images/gw/facilities/bar-lounge.jpg',
    icon: 'fas fa-martini-glass',
    title: 'Bar & Lounge',
    desc: 'Premium drinks & live sport',
  },
];

const testimonials = [
  {
    name: 'Emeka Eze',
    role: 'Business Traveller',
    text: 'The best hotel in Owerri — spotless rooms, excellent service, 24h power and a stunning pool. I stay here every visit and it never disappoints.',
  },
  {
    name: 'Chioma Okafor',
    role: 'Wedding Reception',
    text: 'Our wedding at the Greatwood event hall was breathtaking. The gold-ceiling ballroom is magnificent and every detail was perfect.',
  },
  {
    name: 'Dr. Abubakar Y.',
    role: 'Medical Conference',
    text: 'Outstanding for corporate events. Professional boardroom, uninterrupted power and very comfortable rooms. Five-star experience in Owerri.',
  },
];

/* ─── ROOM CARD ──────────────────────────────── */
function RoomCard({ room }) {
  const { addToCart, cartItems } = useCart();
  const [flash, setFlash] = useState(false);
  const inCart = cartItems.some((i) => i.id === room.id);
  const handleAdd = () => {
    addToCart({ ...room, currency: '₦' });
    setFlash(true);
    setTimeout(() => setFlash(false), 1800);
  };
  return (
    <div className="h2-room-card">
      <div className="h2-room-img">
        <img src={room.img} alt={room.title} />
        <div className="h2-room-overlay">
          <span className="h2-room-tag" style={{ background: room.color }}>
            {room.tag}
          </span>
          <Link to="/rooms" className="h2-room-view">
            View Details →
          </Link>
        </div>
      </div>
      <div className="h2-room-foot">
        <div>
          <h5 className="h2-room-name">{room.title}</h5>
          <span className="h2-room-price">
            {formatCurrencyShort(room.price)}
            <small>/night</small>
          </span>
        </div>
        <button
          className={
            'h2-book-btn' + (inCart ? ' incart' : '') + (flash ? ' added' : '')
          }
          onClick={handleAdd}
        >
          {flash ? '✓ Added' : inCart ? '✓ In Cart' : 'Book'}
        </button>
      </div>
    </div>
  );
}

/* ─── HOME ────────────────────────────────────── */
export default function Home() {
  const ciRef = useRef(null);
  const coRef = useRef(null);
  const [formSent, setFormSent] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    room: 'Greatwood Standard — ₦40,000',
  });

  useEffect(() => {
    new WOW({ mobile: false, live: true }).init();
    Fancybox.bind('[data-fancybox]', {});
    const fi = flatpickr(ciRef.current, {
      dateFormat: 'Y-m-d',
      minDate: 'today',
    });
    const fo = flatpickr(coRef.current, {
      dateFormat: 'Y-m-d',
      minDate: 'today',
    });
    return () => {
      fi.destroy();
      fo.destroy();
      Fancybox.unbind('[data-fancybox]');
    };
  }, []);

  const hf = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submitForm = (e) => {
    e.preventDefault();
    setFormBusy(true);
    setTimeout(() => {
      setFormBusy(false);
      setFormSent(true);
      setForm({
        name: '',
        phone: '',
        checkIn: '',
        checkOut: '',
        room: 'Greatwood Standard — ₦40,000',
      });
      setTimeout(() => setFormSent(false), 6000);
    }, 900);
  };

  return (
    <div className="page-wrapper h2-page">
      <Preloader />
      <Header />

      {/* ══ 1. HERO — split screen ══ */}
      <section className="h2-hero">
        <div
          className="h2-hero-left"
          style={{ backgroundImage: 'url(/images/gw/banner/banner-1.jpg)' }}
        >
          <div className="h2-hero-left-overlay" />
          <div className="h2-hero-badge">
            <i className="fas fa-star"></i>
            <i className="fas fa-star"></i>
            <i className="fas fa-star"></i>
            <i className="fas fa-star"></i>
            <i className="fas fa-star"></i>
            <span>Top-Rated in Owerri</span>
          </div>
        </div>
        <div className="h2-hero-right">
          <div className="h2-hero-content">
            <span className="h2-eyebrow">
              Owerri, Imo State · PH-Owerri Road
            </span>
            <h1 className="h2-hero-title">
              Greatwood
              <br />
              <em>Hotel</em>
            </h1>
            <p className="h2-hero-desc">
              Plot OF/12, Commercial District G,
              <br />
              Opposite Duwez Filling Station, Owerri
            </p>

            {/* Quick booking form */}
            <form className="h2-quick-form" onSubmit={submitForm}>
              {formSent && (
                <div className="h2-form-success">
                  <i className="fas fa-circle-check me-2"></i>Sent! We'll call
                  you shortly.
                </div>
              )}
              <div className="h2-form-row">
                <div className="h2-form-field">
                  <label>Your Name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={hf}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="h2-form-field">
                  <label>Phone Number</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={hf}
                    placeholder="0806 xxx xxxx"
                    required
                  />
                </div>
              </div>
              <div className="h2-form-row">
                <div className="h2-form-field">
                  <label>Check-In</label>
                  <input
                    ref={ciRef}
                    name="checkIn"
                    placeholder="Select date"
                    readOnly
                  />
                </div>
                <div className="h2-form-field">
                  <label>Check-Out</label>
                  <input
                    ref={coRef}
                    name="checkOut"
                    placeholder="Select date"
                    readOnly
                  />
                </div>
              </div>
              <div className="h2-form-field" style={{ marginBottom: 16 }}>
                <label>Room Type</label>
                <select name="room" value={form.room} onChange={hf}>
                  <option>Greatwood Standard — ₦40,000/night</option>
                  <option>Greatwood Executive — ₦50,000/night</option>
                  <option>Greatwood Special — ₦80,000/night</option>
                  <option>Presidential Suite — ₦150,000/night</option>
                </select>
              </div>
              <button type="submit" className="h2-form-btn" disabled={formBusy}>
                {formBusy ? 'Sending…' : 'Request Reservation'}
              </button>
            </form>

            <div className="h2-hero-phones">
              <a href="tel:+2348076988183">
                <i className="fas fa-phone"></i> 0807 698 8183
              </a>
              <a
                href="https://wa.me/2348176291933"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-whatsapp"></i> 0817 629 1933
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══ 2. ROOMS ══ */}
      <section className="h2-section h2-rooms-section">
        <div className="h2-section-head">
          <div>
            <span className="h2-label">Room Tariff</span>
            <h2 className="h2-title">Choose Your Room</h2>
          </div>
          <Link to="/rooms" className="h2-see-all">
            View All Rooms →
          </Link>
        </div>
        <Swiper
          modules={[Autoplay, Navigation]}
          navigation
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          loop
          spaceBetween={20}
          slidesPerView={1}
          breakpoints={{
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1400: { slidesPerView: 4 },
          }}
          className="h2-rooms-swiper"
        >
          {rooms.map((r) => (
            <SwiperSlide key={r.id}>
              <RoomCard room={r} />
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* ══ 3. ABOUT — split ══ */}
      <section className="h2-about">
        <div className="h2-about-images">
          <div
            className="h2-about-img-main"
            style={{
              backgroundImage: 'url(/images/gw/facilities/boardroom.jpg)',
            }}
          />
          <div
            className="h2-about-img-accent"
            style={{
              backgroundImage: 'url(/images/gw/facilities/reception-staff.jpg)',
            }}
          >
            <div className="h2-about-stat">
              <strong>24/7</strong>
              <span>
                At Your
                <br />
                Service
              </span>
            </div>
          </div>
        </div>
        <div className="h2-about-content">
          <span className="h2-label">About Us</span>
          <h2 className="h2-title">A Landmark of Luxury in Owerri</h2>
          <p>
            Greatwood Hotel is one of Imo State's most distinguished hospitality
            destinations — set on manicured grounds along the
            Portharcourt-Owerri Road. We offer world-class rooms, a swimming
            pool, event hall, professional boardroom and a fully stocked bar.
          </p>
          <div className="h2-about-points">
            {[
              '4 room categories from ₦40,000/night',
              'Swimming pool, bar, boardroom & event hall',
              '24-hour power, security & reception',
              'Free WiFi & secure parking',
            ].map((p, i) => (
              <div key={i} className="h2-point">
                <i className="fas fa-circle-check"></i>
                <span>{p}</span>
              </div>
            ))}
          </div>
          <div className="h2-about-actions">
            <Link to="/about" className="h2-btn-primary">
              Our Story
            </Link>
            <Link to="/contact" className="h2-btn-ghost">
              Book a Stay
            </Link>
          </div>
        </div>
      </section>

      {/* ══ 4. FACILITIES ══ */}
      <section className="h2-section h2-facilities-section">
        <div className="h2-section-head">
          <div>
            <span className="h2-label">What We Offer</span>
            <h2 className="h2-title" style={{ color: '#fff' }}>
              Our Facilities
            </h2>
          </div>
          <Link to="/services" className="h2-see-all">
            All Facilities →
          </Link>
        </div>
        <div className="h2-facilities-grid">
          {facilities.map((f, i) => (
            <div key={i} className="h2-facility-card">
              <div className="h2-fac-img">
                <img src={f.img} alt={f.title} />
              </div>
              <div className="h2-fac-body">
                <div className="h2-fac-icon">
                  <i className={f.icon}></i>
                </div>
                <h5>{f.title}</h5>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ 5. TESTIMONIALS + CONTACT STRIP ══ */}
      <section className="h2-bottom">
        <div className="h2-testimonials">
          <span className="h2-label">Guest Reviews</span>
          <h2 className="h2-title" style={{ marginBottom: 28 }}>
            What Guests Say
          </h2>
          <Swiper
            modules={[Autoplay]}
            autoplay={{ delay: 5000 }}
            loop
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{ 768: { slidesPerView: 2 } }}
            className="h2-testi-swiper"
          >
            {testimonials.map((t, i) => (
              <SwiperSlide key={i}>
                <div className="h2-testi-card">
                  <div className="h2-testi-stars">
                    {[0, 1, 2, 3, 4].map((k) => (
                      <i key={k} className="fas fa-star"></i>
                    ))}
                  </div>
                  <p>"{t.text}"</p>
                  <div className="h2-testi-author">
                    <div className="h2-testi-av">{t.name[0]}</div>
                    <div>
                      <strong>{t.name}</strong>
                      <span>{t.role}</span>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Contact strip */}
        <div className="h2-contact-strip">
          <div className="h2-cs-info">
            <img
              src="/images/gw/logo.png"
              alt="Greatwood Hotel"
              className="h2-cs-logo"
            />
            <div>
              <h4>Greatwood Hotel</h4>
              <p>
                Plot OF/12, Commercial District G, Along PH-Owerri Road (Opp.
                Duwez Filling Station), Owerri, Imo State
              </p>
            </div>
          </div>
          <div className="h2-cs-phones">
            <a href="tel:+2348076988183">
              <i className="fas fa-phone"></i> 0807 698 8183
            </a>
            <a
              href="https://wa.me/2348176291933"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-whatsapp"></i> 0817 629 1933
            </a>
            <a href="mailto:">
              <i className="fas fa-envelope"></i> Email Us
            </a>
          </div>
          <Link to="/contact" className="h2-btn-primary">
            Full Contact & Map
          </Link>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
